import { Fragment, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import {
  ArrowPathRoundedSquareIcon,
  BriefcaseIcon,
  CheckBadgeIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  DocumentPlusIcon,
  DocumentDuplicateIcon,
  FlagIcon,
  FolderOpenIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { TWclassNames } from "../../../utils/Div";
import { useUserHasAccess } from "../../../../routes/roleOnly";
import { ReportStatusDialog } from "./report-status-dialog";
import { ReportResetDialog } from "./report-reset-dialog";
import { ReportRectifyDialog } from "./report-rectify-dialog";
import { ReportOrderDialog } from "./report-order-dialog";
import CaseDialog from "../../cases/case-dialog";
import { PMDialog } from "../../../utils/PMDialog";
import { fetchModuleTagMentions } from "../../../../api/module_tags/mentions";
import { ResourceLoading } from "../../../utils/ResourceLoading";

/**
 * ReportAdminMenu - Admin actions (status, reset, case, sequel, mentions, order).
 * Desktop: sticky sidebar. Mobile: floating button + slide-in panel from the right.
 */
export const ReportAdminMenu = ({ report, onRefresh, isLoading = false }) => {
  const { t } = useTranslation();
  const hasAdminAccess = useUserHasAccess({ role: "service", action: "reset" });

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [rectifyDialogOpen, setRectifyDialogOpen] = useState(false);
  const [caseDialogOpen, setCaseDialogOpen] = useState(false);
  const [mentionsDialogOpen, setMentionsDialogOpen] = useState(false);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [mentions, setMentions] = useState([]);
  const [mentionsLoading, setMentionsLoading] = useState(false);

  useEffect(() => {
    if (mentionsDialogOpen && report?.id) {
      setMentionsLoading(true);
      fetchModuleTagMentions({ module: "report", id: report.id })
        .then((res) => {
          setMentions(res?.data?.data || []);
        })
        .catch(() => setMentions([]))
        .finally(() => setMentionsLoading(false));
    }
  }, [mentionsDialogOpen, report?.id]);

  const getMentionUrl = (mentionItem) => {
    if (mentionItem?.type === "case") {
      return `/dashboard/cases/${mentionItem?.object?.id}`;
    }
    if (mentionItem?.type === "note") {
      switch (mentionItem?.module) {
        case "report":
          return `/dashboard/service/reports/${mentionItem?.module_param}`;
        case "work_order":
          return `/dashboard/work_orders/${mentionItem?.module_param}`;
        case "project":
          return `/dashboard/projects/${mentionItem?.module_param}`;
        case "customer":
          return `/dashboard/customers/${mentionItem?.module_param}`;
        case "contact":
          return `/dashboard/contacts?contactId=${mentionItem?.module_param}`;
        case "service_agreement":
          return `/dashboard/service/agreements/${mentionItem?.module_param}`;
        case "order":
          return `/dashboard/orders/${mentionItem?.module_param}`;
        case "product":
          return `/dashboard/products/${mentionItem?.module_param}`;
        case "folder":
          return `/dashboard/folders/${mentionItem?.module_param}`;
        default:
          return "/";
      }
    }
    return "/";
  };

  const closeMobileDrawer = () => setMobileDrawerOpen(false);

  const runWithOptionalClose = (fn) => {
    return () => {
      fn();
      closeMobileDrawer();
    };
  };

  if (!hasAdminAccess || !report?.id) {
    return null;
  }

  const menuItems = [
    {
      id: "status",
      label: t("report.admin.status"),
      description: t("report.admin.status_desc"),
      icon: CheckBadgeIcon,
      action: runWithOptionalClose(() => setStatusDialogOpen(true)),
      color: "text-blue-600 hover:bg-blue-50",
    },
    {
      id: "reset",
      label: t("report.admin.reset"),
      description: t("report.admin.reset_desc"),
      icon: ArrowPathRoundedSquareIcon,
      action: runWithOptionalClose(() => setResetDialogOpen(true)),
      color: "text-amber-600 hover:bg-amber-50",
    },
    report?.finished && {
      id: "create_order",
      label: t("report.admin.create_order"),
      description: t("report.admin.create_order_desc"),
      icon: ClipboardDocumentListIcon,
      action: runWithOptionalClose(() => setOrderDialogOpen(true)),
      color: "text-emerald-600 hover:bg-emerald-50",
    },
    report?.case?.id
      ? {
          id: "view_case",
          label: t("report.admin.view_case"),
          description: report?.case?.displayTitle,
          icon: BriefcaseIcon,
          path: `/dashboard/cases/${report.case.id}`,
          external: true,
          color: "text-indigo-600 hover:bg-indigo-50",
        }
      : {
          id: "create_case",
          label: t("report.admin.create_case"),
          description: t("report.admin.create_case_desc"),
          icon: BriefcaseIcon,
          action: runWithOptionalClose(() => setCaseDialogOpen(true)),
          color: "text-indigo-600 hover:bg-indigo-50",
        },
    report?.sequelMainReport?.id && {
      id: "view_main",
      label: t("report.admin.view_main_report"),
      description: report?.sequelMainReport?.displayTitle,
      icon: FolderOpenIcon,
      path: `/dashboard/service/reports/${report.sequelMainReport.id}`,
      color: "text-gray-700 hover:bg-gray-50",
    },
    report?.sequelReport?.id
      ? {
          id: "view_sequel",
          label: t("report.admin.view_sequel"),
          description: report?.sequelReport?.report_number,
          icon: FolderOpenIcon,
          path: `/dashboard/service/reports/${report.sequelReport.id}`,
          color: "text-gray-700 hover:bg-gray-50",
        }
      : {
          id: "create_sequel",
          label: t("report.admin.create_sequel"),
          description: t("report.admin.create_sequel_desc"),
          icon: DocumentPlusIcon,
          action: runWithOptionalClose(() => setRectifyDialogOpen(true)),
          color: "text-green-600 hover:bg-green-50",
        },
    {
      id: "mentions",
      label: t("report.admin.open_mentions"),
      description: t("report.admin.open_mentions_desc"),
      icon: DocumentDuplicateIcon,
      action: runWithOptionalClose(() => setMentionsDialogOpen(true)),
      color: "text-purple-600 hover:bg-purple-50",
    },
  ].filter(Boolean);

  const renderMenuRows = (opts = { closeOnNavigate: false }) => (
    <div className="divide-y divide-gray-100 p-2">
      {menuItems.map((item) => (
        <div key={item.id}>
          {item.path ? (
            <Link
              to={item.path}
              target={item.external ? "_blank" : undefined}
              rel={item.external ? "noopener noreferrer" : undefined}
              onClick={opts.closeOnNavigate ? closeMobileDrawer : undefined}
              className={TWclassNames(
                "flex items-start gap-3 rounded-lg px-3 py-3 transition-colors sm:py-2.5",
                item.color
              )}
            >
              <item.icon className="mt-0.5 h-6 w-6 flex-shrink-0 sm:h-5 sm:w-5" />
              <div className="min-w-0 flex-1">
                <p className="text-base font-medium sm:text-sm">{item.label}</p>
                {item.description && (
                  <p className="mt-0.5 truncate text-xs text-gray-500">
                    {item.description}
                  </p>
                )}
              </div>
              <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-gray-400 sm:h-4 sm:w-4" />
            </Link>
          ) : (
            <button
              type="button"
              onClick={item.action}
              className={TWclassNames(
                "flex w-full items-start gap-3 rounded-lg px-3 py-3 text-left transition-colors sm:py-2.5",
                item.color
              )}
            >
              <item.icon className="mt-0.5 h-6 w-6 flex-shrink-0 sm:h-5 sm:w-5" />
              <div className="min-w-0 flex-1">
                <p className="text-base font-medium sm:text-sm">{item.label}</p>
                {item.description && (
                  <p className="mt-0.5 truncate text-xs text-gray-500">
                    {item.description}
                  </p>
                )}
              </div>
              <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-gray-400 sm:h-4 sm:w-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  );

  const menuCardHeader = (
    <div className="border-b border-gray-100 px-4 py-3">
      <div className="flex items-center gap-2">
        <FlagIcon className="h-5 w-5 text-gray-500" />
        <h3 className="text-sm font-semibold text-gray-800">
          {t("report.admin.title")}
        </h3>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden lg:block">
        <div className="sticky top-4 rounded-xl border border-gray-200 bg-white shadow-sm">
          {menuCardHeader}
          {renderMenuRows({ closeOnNavigate: false })}
        </div>
      </div>

      {/* Mobile: FAB + drawer portaled so parent `hidden lg:flex` does not suppress them */}
      {typeof document !== "undefined" &&
        createPortal(
          <>
            <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[45] flex justify-end px-4 pb-[max(4.75rem,calc(3.5rem+env(safe-area-inset-bottom)))] pt-2 lg:hidden">
              <button
                type="button"
                onClick={() => setMobileDrawerOpen(true)}
                className="pointer-events-auto flex h-14 w-14 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-800 shadow-lg ring-1 ring-black/5 transition hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                aria-label={t("report.admin.title")}
              >
                <FlagIcon className="h-7 w-7" aria-hidden />
              </button>
            </div>

            <Transition.Root show={mobileDrawerOpen} as={Fragment}>
              <Dialog
                as="div"
                className="relative z-[55] lg:hidden"
                onClose={setMobileDrawerOpen}
              >
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="fixed inset-0 bg-black/40" aria-hidden />
                </Transition.Child>

                <div className="fixed inset-0 overflow-hidden">
                  <div className="absolute inset-0 overflow-hidden">
                    <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                      <Transition.Child
                        as={Fragment}
                        enter="transform transition ease-out duration-300"
                        enterFrom="translate-x-full"
                        enterTo="translate-x-0"
                        leave="transform transition ease-in duration-200"
                        leaveFrom="translate-x-0"
                        leaveTo="translate-x-full"
                      >
                        <Dialog.Panel className="pointer-events-auto flex h-full w-screen max-w-md flex-col border-l border-gray-200 bg-white shadow-2xl">
                          <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-4 py-3">
                            <div className="flex items-center gap-2">
                              <FlagIcon className="h-5 w-5 text-gray-500" />
                              <Dialog.Title className="text-base font-semibold text-gray-900">
                                {t("report.admin.title")}
                              </Dialog.Title>
                            </div>
                            <button
                              type="button"
                              className="rounded-full p-2 text-gray-500 hover:bg-gray-100"
                              onClick={closeMobileDrawer}
                              aria-label={t("Close", { defaultValue: "Lukk" })}
                            >
                              <XMarkIcon className="h-6 w-6" aria-hidden />
                            </button>
                          </div>
                          <div className="min-h-0 flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)]">
                            {renderMenuRows({ closeOnNavigate: true })}
                          </div>
                        </Dialog.Panel>
                      </Transition.Child>
                    </div>
                  </div>
                </div>
              </Dialog>
            </Transition.Root>
          </>,
          document.body
        )}

      <ReportStatusDialog
        report={report}
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        onRefresh={(data) => {
          onRefresh?.(data);
          setStatusDialogOpen(false);
        }}
      />

      <ReportResetDialog
        report={report}
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        onRefresh={(data) => {
          onRefresh?.(data);
          setResetDialogOpen(false);
        }}
      />

      <ReportRectifyDialog
        report={report}
        open={rectifyDialogOpen}
        onClose={() => setRectifyDialogOpen(false)}
        onRefresh={() => onRefresh?.()}
      />

      <CaseDialog
        open={caseDialogOpen}
        selected={report?.case}
        onClose={() => setCaseDialogOpen(false)}
        report={report}
        customer={report?.customer}
        contact={report?.contact}
        title={
          t("report.admin.case_dialog_title") +
          " | " +
          (report?.displayTitle || report?.report_number)
        }
        onRefresh={() => onRefresh?.()}
      />

      <ReportOrderDialog
        report={report}
        open={orderDialogOpen}
        isLoading={isLoading}
        onClose={() => setOrderDialogOpen(false)}
        onRefresh={onRefresh}
      />

      <PMDialog
        title={t("report.admin.mentions_dialog_title")}
        open={mentionsDialogOpen}
        onClose={() => setMentionsDialogOpen(false)}
      >
        {mentionsLoading ? (
          <ResourceLoading />
        ) : mentions?.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {mentions.map((mentionItem) => (
              <Link
                key={mentionItem.id}
                to={getMentionUrl(mentionItem)}
                target="_blank"
                className="block px-4 py-3 hover:bg-gray-50"
              >
                <p className="font-medium text-gray-900">
                  {mentionItem?.type === "note"
                    ? `${t("note")} (${t(mentionItem?.module)})`
                    : t(mentionItem?.type)}
                </p>
                <p className="text-sm text-gray-500">
                  {mentionItem?.type === "note"
                    ? mentionItem?.object?.note
                    : mentionItem?.object?.displayTitle}
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  {t("report.admin.mentioned_at")}{" "}
                  {new Date(mentionItem.created_at).toLocaleString("nb-NO")}
                </p>
              </Link>
            ))}
          </div>
        ) : (
          <p className="px-4 py-6 text-center text-sm text-gray-500">
            {t("report.admin.no_mentions")}
          </p>
        )}
      </PMDialog>
    </>
  );
};
